/// <reference types="office-js" />
/**
 * Pure theme-resolution helpers.
 *
 * Outlook does not support `officeTheme.isDarkTheme` / `themeId` — the only
 * signal is the theme's color values, so dark detection derives from the
 * relative luminance of `bodyBackgroundColor`.
 */

export type ResolvedTheme = 'dark' | 'light';

export type ThemeSource = 'office' | 'system';

const HEX_COLOR_REGEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * Whether a hex color is dark, by relative luminance (0-255 scale, cutoff at
 * 128). Returns null for unparseable input so callers can fall back.
 */
export function isColorDark(hex: string | null | undefined): boolean | null {
  if (!hex || !HEX_COLOR_REGEX.test(hex)) {
    return null;
  }
  let digits = hex.slice(1);
  if (digits.length === 3) {
    digits = [...digits].map((c) => c + c).join('');
  }
  const r = Number.parseInt(digits.slice(0, 2), 16);
  const g = Number.parseInt(digits.slice(2, 4), 16);
  const b = Number.parseInt(digits.slice(4, 6), 16);
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance < 128;
}

/**
 * Resolve light/dark from an Office theme object. Null when the theme (or its
 * background color) is unavailable or unparseable.
 */
export function resolveThemeFromOfficeTheme(
  officeTheme: Pick<Office.OfficeTheme, 'bodyBackgroundColor'> | null | undefined,
): ResolvedTheme | null {
  const dark = isColorDark(officeTheme?.bodyBackgroundColor);
  if (dark === null) {
    return null;
  }
  return dark ? 'dark' : 'light';
}

/**
 * The OS/browser preference — the fallback when the Outlook theme is unknown
 * (older hosts, mobile, plain-browser dev).
 */
export function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
